import React, { useMemo, useState } from 'react';
import { SelectPopover } from '@planne-software/uni/SelectPopover';
import { useAppSelector, useTranslate, useWidgetParam } from '@src/core/hooks';
import { useWidgetId } from '@src/core/IdProvider';
import { currentProductAdderStatus, productSelector, checkedAdditionalsCountSelector } from '@src/stores/product';
import { Typography } from '@planne-software/uni/mui/material';
import { useGuardedSequentialOpenChange } from '../../hooks/useGuardedSequentialOpenChange';
import { AdditionalRow } from './AdditionalRow';
import { AdditionalsFooter } from './AdditionalsFooter';
import { ListArea } from './styles';
import { SequentialSlotShell } from '../SequentialSlotShell';

const AdditionalsSelector = () => {
  const widgetId = useWidgetId();
  const { i18n } = useTranslate();
  const productId = useWidgetParam('product-id');
  const [open, setOpen] = useState(false);
  const currentStatus = useAppSelector(currentProductAdderStatus(widgetId));
  const isAddingToCart = useMemo(() => currentStatus === 'adding_to_cart', [currentStatus]);
  const product = useAppSelector(productSelector(productId ?? ''));
  const additionals = product?.additionals ?? [];

  const checkedAdditionalsCount = useAppSelector(checkedAdditionalsCountSelector(widgetId, productId));

  const guardedOnOpenChange = useGuardedSequentialOpenChange('additionals', setOpen);

  if (!productId || additionals.length === 0) {
    return null;
  }

  const selectedTriggerLabel =
    checkedAdditionalsCount > 0 ? (
      <Typography component='span' sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {i18n('product_booking_additionals_summary', { count: checkedAdditionalsCount })}
      </Typography>
    ) : null;

  return (
    <SequentialSlotShell slot='additionals'>
      <SelectPopover
        name='product-booking-additionals'
        fullWidth
        disabled={isAddingToCart}
        disablePopover={isAddingToCart}
        isBottomSheet
        placeholder={i18n('product_booking_additionals')}
        selectedElement={selectedTriggerLabel}
        bottomSheetTitle={i18n('adder_additionals')}
        open={open}
        onOpenChange={guardedOnOpenChange}
        footer={<AdditionalsFooter widgetId={widgetId} productId={productId} onConfirmed={() => setOpen(false)} />}
      >
        <ListArea>
          {additionals.map((additional) => (
            <AdditionalRow
              key={additional.id}
              additional={additional}
              widgetId={widgetId}
              productId={productId}
              i18n={i18n}
            />
          ))}
        </ListArea>
      </SelectPopover>
    </SequentialSlotShell>
  );
};

export default AdditionalsSelector;
